import {
  addDiscountCode,
  invalidateDiscountCode,
  recalculatePrices,
  resetDiscountCode,
  resetVoucherCode,
  setAllPrices,
  setIsDiscounted,
} from './action';
import fetch from '../../../../js/api/fetch';
import { addError } from '../MetaData/action';
import { updateRootState } from '../../functions/updateRootState';
import type { TypedDispatch, TypedMiddleware } from '../store';
import type { Price } from '../../../../ssr/src/Outlet/ShoppingCart/ShoppingCartSchema';

type PricesResponse = {
  prices: {
    discount: number;
    shippingPrice: number;
    subTotal: number;
    total: number;
  };
};

export const isPricesResponse = (json: unknown): json is PricesResponse => {
  if (
    typeof json !== 'object' ||
    json === null ||
    typeof (json as { prices?: unknown })?.prices !== 'object' ||
    (json as { prices?: unknown }).prices === null ||
    (json as { prices?: unknown }).prices === null
  ) {
    return false;
  }

  const { prices } = json as {
    prices: {
      discount?: number;
      shippingPrice?: number;
      subTotal?: number;
      total?: number;
    };
  };

  return (
    typeof prices.shippingPrice === 'number' &&
    typeof prices.discount === 'number' &&
    typeof prices.subTotal === 'number' &&
    typeof prices.total === 'number'
  );
};

const reduceAmountValueToTotal = (
  total: number,
  item: { amount: number; price: Price },
): number => total + item.amount * item.price.value;

export const recalculateAllPricesMiddleware: TypedMiddleware =
  (storeApi) => (next) => (action) => {
    if (recalculatePrices.match(action)) {
      const state = storeApi.getState();
      const { items } = state.cartItems;
      const { groups } = state.upsells;

      const availableUpsellsTotalPrices = Object.values(groups).reduce<number>(
        (totalUpsellPrice, upsellGroups) =>
          totalUpsellPrice +
          upsellGroups.reduce<number>(
            (totalGroupPrice, upsellGroup) =>
              totalGroupPrice +
              upsellGroup.products.reduce<number>(
                (totalProductsPrice, upsellProduct) =>
                  totalProductsPrice +
                  upsellProduct.amountInCart * upsellProduct.price.numericValue,
                0,
              ),
            0,
          ),
        0,
      );

      const cartItemTotalPrices = Object.values(items).reduce(
        reduceAmountValueToTotal,
        0,
      );

      const subTotal = cartItemTotalPrices + availableUpsellsTotalPrices;

      const totalDiscount =
        state.prices.discount.value + state.prices.voucher.value;

      const total = subTotal - totalDiscount + state.prices.shippingPrice.value;

      storeApi.dispatch(
        setAllPrices({
          discount: state.prices.discount.value,
          shippingPrice: state.prices.shippingPrice.value,
          subTotal,
          total,
          voucher: state.prices.voucher.value,
        }),
      );
    }

    return next(action);
  };

export const hasStatus = (json: unknown): json is { status: string } => {
  if (typeof json !== 'object') {
    return false;
  }

  return typeof (json as { status?: string } | null)?.status === 'string';
};

const addDiscountCodeCall = async (
  dispatch: TypedDispatch,
  code: string,
): Promise<void> => {
  const response = await fetch('checkout/addDiscount', {
    body: JSON.stringify({ discountCode: code }),
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
    },
    method: 'POST',
  });

  const json: unknown = await response.json();

  if (hasStatus(json) && json.status === 'OK') {
    dispatch(setIsDiscounted({ hasDiscount: true }));
    await updateRootState(dispatch);
  } else {
    dispatch(invalidateDiscountCode());
  }
};

export const addDiscountCodeMiddleware: TypedMiddleware =
  (storeApi) => (next) => (action) => {
    if (addDiscountCode.match(action)) {
      addDiscountCodeCall(storeApi.dispatch, action.payload.code).catch(() =>
        storeApi.dispatch(addError()),
      );
    }
    return next(action);
  };

const resetCode = async (
  dispatch: TypedDispatch,
  url: string,
): Promise<void> => {
  const response = await fetch(url, {
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
    },
    method: 'POST',
  });

  const json: unknown = await response.json();

  if (hasStatus(json) && json.status === 'OK') {
    dispatch(setIsDiscounted({ hasDiscount: false }));
    await updateRootState(dispatch);
  }
};

const resetDiscountCodeCall = (dispatch: TypedDispatch) =>
  resetCode(dispatch, 'checkout/resetDiscount');
const resetGiftVoucherCall = (dispatch: TypedDispatch) =>
  resetCode(dispatch, 'checkout/resetGiftvoucher');

export const resetDiscountCodeMiddleware: TypedMiddleware =
  (storeApi) => (next) => (action) => {
    if (resetDiscountCode.match(action)) {
      resetDiscountCodeCall(storeApi.dispatch).catch(() =>
        storeApi.dispatch(addError()),
      );

      const result = next(action);
      storeApi.dispatch(recalculatePrices());
      return result;
    }
    return next(action);
  };

export const resetVoucherCodeMiddleware: TypedMiddleware =
  (storeApi) => (next) => (action) => {
    if (resetVoucherCode.match(action)) {
      resetGiftVoucherCall(storeApi.dispatch).catch(() =>
        storeApi.dispatch(addError()),
      );

      const result = next(action);
      storeApi.dispatch(recalculatePrices());
      return result;
    }
    return next(action);
  };
